import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  CheckCircle, XCircle, MinusCircle, Scale, Cpu, Shield, BookOpen,
  Award, Users, MessageCircle, Calendar, ChevronRight, Star, Gavel,
  Globe, Zap, Lock, TrendingUp, Phone, Mail, PlayCircle, Video, Clock, AlertTriangle
} from 'lucide-react';
import { UpsellModal } from './landing/UpsellModal';
import { ConsentModal } from './landing/ConsentModal';
import { UrgencyBanner } from './landing/UrgencyBanner';
import {
  CONSENT_ITEMS_BY_COURSE,
  PURCHASE_LINKS as LINKS,
  COMBO_TOTAL_VAGAS,
  getUrgencyConfig,
} from './landing/config';

const PROFESSOR_IMAGE = "https://customer-assets.emergentagent.com/job_793c023d-2f91-47b0-b80f-574f652f8180/artifacts/vvwm2ja2_Professor.jpg";
const POLITALKS_LOGO = "https://customer-assets.emergentagent.com/job_digital-law-ai-1/artifacts/zrhe5z43_LOGO%20politalks%201%20transparente.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const propagandaCalendar = [
  { date: "08/06", full: "08/06/2026", content: "Abertura do curso + Tipologia da propaganda política: intrapartidária, partidária, pré-campanha e propaganda eleitoral" },
  { date: "15/06", full: "15/06/2026", content: "Propaganda antecipada, promoção pessoal, pedido explícito de voto e evolução jurisprudencial" },
  { date: "22/06", full: "22/06/2026", content: "Pré-campanha, lives, eventos, reuniões, manifestações públicas e publicidade institucional" },
  { date: "29/06", full: "29/06/2026", content: "Propaganda de rua: regras gerais, bens públicos, bens particulares e bens de uso comum — Dicas Valiosas" },
  { date: "06/07", full: "06/07/2026", content: "Comitês, bandeiras, mesas, adesivos, propaganda móvel e distribuição de material em espaços públicos abertos" },
  { date: "13/07", full: "13/07/2026", content: "Material impresso, santinhos, volantes, derrame de propaganda, Braille e IA em impressos" },
  { date: "20/07", full: "20/07/2026", content: "Propaganda sonora: carros de som, alto-falantes, comícios, eventos e limites de horário/local" },
  { date: "27/07", full: "27/07/2026", content: "Ambiente de trabalho, assédio eleitoral, espaços sensíveis, universidades, escolas e entidades" },
  { date: "03/08", full: "03/08/2026", content: "Rádio, TV, plano de mídia, diversidade, mulheres, negros, indígenas e compensação semanal de tempo" },
  { date: "10/08", full: "10/08/2026", content: "Poder de polícia, direito de resposta, interface com internet/IA e aula prática de casos" },
];

const propagandaHighlights = [
  "Propaganda antecipada e promoção pessoal",
  "Propaganda em bens públicos e particulares",
  "Comitês, adesivos, bandeiras e material impresso",
  "Propaganda sonora, carros de som e comícios",
  "Assédio eleitoral no ambiente de trabalho",
  "Rádio, TV, diversidade e propaganda inclusiva",
  "Atualizado com a Res. TSE nº 23.755/2026",
];

const digitalHighlights = [
  "Propaganda eleitoral na internet",
  "IA, deepfakes e conteúdo sintético",
  "Impulsionamento e influenciadores",
  "Plataformas digitais e moderação",
  "WhatsApp, Telegram e disparos",
  "Prova digital e identificação de infratores",
  "Atualizado com a Res. TSE nº 23.755/2026",
];

const propagandaModules = [
  { title: "Módulo 1 — Tipologia da Propaganda Política e Pré-Campanha", topics: ["Propaganda intrapartidária, partidária e eleitoral", "Propaganda antecipada e promoção pessoal", "Lives de pré-campanha", "Publicidade institucional", "Perspectiva de gênero e raça"] },
  { title: "Módulo 2 — Propaganda de Rua, Bens Públicos, Particulares e Comitês", topics: ["Bens públicos, particulares e de uso comum", "Bandeiras, mesas, adesivos e propaganda móvel", "Distribuição de material em praças, feiras, parques e vias públicas", "Comitês eleitorais", "Derrame de material e responsabilização"] },
  { title: "Módulo 3 — Material Impresso, Acessibilidade e Propaganda Sonora", topics: ["Santinhos, volantes, adesivos e impressos", "Requisitos legais dos materiais", "Material em Braille", "IA em material impresso", "Carros de som, alto-falantes e comícios"] },
  { title: "Módulo 4 — Ambiente de Trabalho, Assédio Eleitoral e Espaços Sensíveis", topics: ["Propaganda no trabalho", "Assédio eleitoral", "Empresas, repartições e sindicatos", "Universidades, escolas e manifestações espontâneas"] },
  { title: "Módulo 5 — Rádio, TV, Diversidade e Propaganda Inclusiva", topics: ["Horário eleitoral gratuito", "Plano de mídia", "Tempo para mulheres, pessoas negras e indígenas", "Compensação semanal de tempo", "Propaganda inclusiva"] },
  { title: "Módulo 6 — Interface com Internet, IA e Poder de Polícia", topics: ["Influenciadores", "Impulsionamento", "IA e conteúdo sintético", "Poder de polícia", "Direito de resposta", "Remoção de conteúdo"] },
];

const digitalModules = [
  { title: "Módulo 1 — Propaganda Eleitoral Digital e Pré-Campanha na Internet", topics: ["Propaganda eleitoral na internet", "Propaganda intrapartidária digital", "Pré-campanha digital", "Lives e retransmissão", "Endereços eletrônicos de campanha"] },
  { title: "Módulo 2 — Impulsionamento, Publicidade Digital e Influenciadores", topics: ["Impulsionamento político-eleitoral", "Contratação direta com provedores", "Repositório público de anúncios", "IA em impulsionamentos", "Influenciadores e vantagem econômica"] },
  { title: "Módulo 3 — Inteligência Artificial, Conteúdo Sintético e Deepfakes", topics: ["IA generativa nas campanhas", "Conteúdo sintético", "Rotulagem obrigatória", "Deepfakes e manipulação digital", "Janela crítica de 72 horas", "Inversão do ônus da prova"] },
  { title: "Módulo 4 — Provedores, Plataformas e Moderação", topics: ["Provedores de conexão e aplicação", "Deveres das plataformas", "Compliance de plataformas", "Remoção de conteúdo", "Perfis falsos, robôs e contas apócrifas"] },
  { title: "Módulo 5 — Mensageria Privada, Disparos e Comunicação Automatizada", topics: ["WhatsApp, Telegram, SMS e canais", "Comunicação privada consensual", "Grupos restritos", "Disparos automatizados", "Investigação e documentação de mensagens"] },
  { title: "Módulo 6 — Prova Digital, OSINT e Identificação de Infratores", topics: ["Prints, vídeos de tela, atas e hashes", "URL, URI e identificadores", "Noções de OSINT eleitoral", "IPs, logs e portas lógicas", "Quebra de sigilo e identificação"] },
  { title: "Módulo 7 — Desinformação, Liberdade de Expressão e Poder de Polícia", topics: ["Crítica política, sátira e humor", "Desinformação eleitoral", "Ataques ao sistema eletrônico de votação", "Poder de polícia", "Direito de resposta"] },
  { title: "Módulo 8 — Estudos de Caso e Aplicação Prática", topics: ["IA e deepfakes", "Impulsionamento irregular", "Influenciadores", "Mensageria", "Remoção de conteúdo", "Relatório técnico-jurídico"] },
];

const comparisonRows = [
  { feature: "Carga horária", prop: "20h", dig: "20h", combo: "40h" },
  { feature: "Certificado", prop: "20h", dig: "20h", combo: "40h" },
  { feature: "Atualização Res. TSE 23.755/2026", prop: "yes", dig: "yes", combo: "yes" },
  { feature: "Propaganda antecipada", prop: "yes", dig: "partial", combo: "yes" },
  { feature: "Propaganda de rua", prop: "yes", dig: "no", combo: "yes" },
  { feature: "Comitês, bens públicos e particulares", prop: "yes", dig: "no", combo: "yes" },
  { feature: "Rádio e TV", prop: "yes", dig: "no", combo: "yes" },
  { feature: "Inteligência Artificial", prop: "partial", dig: "yes", combo: "yes" },
  { feature: "Deepfakes", prop: "partial", dig: "yes", combo: "yes" },
  { feature: "Impulsionamento", prop: "partial", dig: "yes", combo: "yes" },
  { feature: "Plataformas digitais", prop: "partial", dig: "yes", combo: "yes" },
  { feature: "Prova digital", prop: "partial", dig: "yes", combo: "yes" },
  { feature: "Grupo exclusivo de WhatsApp", prop: "no", dig: "no", combo: "yes" },
  { feature: "Propaganda: 100% gravado (até 31/12)", prop: "yes", dig: "no", combo: "yes" },
  { feature: "Direito Digital: gravação disponível (até 31/12)", prop: "no", dig: "no", combo: "yes" },
  { feature: "Atualizações até 20/12/2026", prop: "yes", dig: "yes", combo: "yes" },
  { feature: "Melhor custo-benefício", prop: "no", dig: "no", combo: "yes" },
];

const faqItems = [
  { q: "Posso comprar apenas um curso?", a: "Sim. Os cursos podem ser adquiridos separadamente. O Curso de Propaganda Eleitoral Clássica custa R$ 999,00 e o Curso de Direito Digital e IA custa R$ 2.000,00." },
  { q: "Posso comprar os dois cursos em combo?", a: "Sim. O combo inclui os dois cursos por R$ 2.699,00, com economia de R$ 300,00 em relação à compra separada." },
  { q: "Qual é a carga horária?", a: "Cada curso possui 20 horas. No combo, a formação total é de 40 horas, com certificado único de 40 horas." },
  { q: "Há certificado?", a: "Sim. Cada curso possui certificado individual de 20 horas. No combo, o aluno recebe certificado total de 40 horas." },
  { q: "Quais são as formas de pagamento?", a: "O pagamento poderá ser feito em até 6x sem juros no cartão de crédito ou à vista via Pix ou Boleto." },
  { q: "Os cursos estão atualizados para 2026?", a: "Sim. Os cursos incorporam as alterações da Resolução TSE nº 23.755/2026 e foram estruturados com foco nos principais desafios das eleições de 2026." },
  { q: "O Curso de Propaganda Eleitoral Clássica é ao vivo ou gravado?", a: "O Curso de Propaganda Eleitoral Clássica é 100% assíncrono (gravado). As aulas são entregues gradualmente conforme os processos eleitorais são julgados pelo TSE, garantindo conteúdo hiper-atualizado. As gravações ficam disponíveis para todos os alunos até 31 de dezembro de 2026." },
  { q: "Quando acontece o Curso de Direito Digital e IA?", a: "O Curso de Direito Digital e IA nas Campanhas de 2026 acontece ao vivo de 06 a 10 de julho de 2026, das 18h às 22h (diariamente). As aulas serão gravadas e disponibilizadas até 31 de dezembro de 2026 exclusivamente para alunos que adquirirem o Combo." },
  { q: "O grupo de WhatsApp está incluído para todos?", a: "O grupo exclusivo de WhatsApp é uma vantagem exclusiva para alunos que adquirirem o combo." },
  { q: "O Curso de Direito Digital tem aulas gravadas?", a: "Sim, mas apenas para alunos que adquirirem o combo. Os alunos do combo poderão assistir às aulas do Curso de Direito Digital gravadas até 31 de dezembro de 2026. Quem adquirir o Direito Digital isoladamente não terá acesso às gravações." },
  { q: "Até quando haverá atualizações?", a: "Os alunos receberão atualizações relacionadas ao conteúdo adquirido até 20 de dezembro de 2026." },
  { q: "Quem pode comprar apenas um curso, pode depois adquirir o outro?", a: "Sim. Entre em contato com nossa equipe para informações sobre a migração para o combo com desconto." },
  { q: "Os cursos são indicados para quem?", a: "São indicados para advogados eleitorais, magistrados, membros do Ministério Público, servidores da Justiça Eleitoral, dirigentes partidários, coordenadores de campanha, assessores jurídicos, consultores eleitorais e profissionais de comunicação política." },
];

const CellIcon = ({ value }) => {
  if (value === 'yes') return <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto" />;
  if (value === 'no') return <XCircle className="w-5 h-5 text-slate-600 mx-auto" />;
  if (value === 'partial') return <MinusCircle className="w-5 h-5 text-amber-400 mx-auto" />;
  return <span className="text-white font-bold text-sm">{value}</span>;
};

export const LandingPage = () => {
  const [upsellModal, setUpsellModal] = useState(null);
  const [showFloat, setShowFloat] = useState(false);
  const [consentModal, setConsentModal] = useState(null); // null | 'propaganda' | 'digital' | 'combo'
  const [consentChecked, setConsentChecked] = useState({});
  const [comboSales, setComboSales] = useState({
    combo_count: 0,
    combo_total: COMBO_TOTAL_VAGAS,
    combo_remaining: COMBO_TOTAL_VAGAS,
  });

  // Listener de scroll (mostra/oculta CTA flutuante)
  useEffect(() => {
    const onScroll = () => setShowFloat(window.scrollY > 700);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Busca contador de vagas do combo a cada 60s
  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/vendas-eleicoes/stats`);
        if (cancelled || !res?.data) return;
        setComboSales({
          combo_count: res.data.combo_count ?? 0,
          combo_total: res.data.combo_total ?? COMBO_TOTAL_VAGAS,
          combo_remaining: res.data.combo_remaining ?? Math.max(0, COMBO_TOTAL_VAGAS - (res.data.combo_count ?? 0)),
        });
      } catch (err) {
        console.error('Falha ao buscar contador de vagas:', err);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Configuração dinâmica do nível de urgência baseado em vagas restantes (helper puro)
  const urgencyConfig = getUrgencyConfig(comboSales.combo_remaining);
  const comboPercent = Math.min(100, Math.round((comboSales.combo_count / comboSales.combo_total) * 100));

  // Handlers — todos useCallback para estabilidade entre renders
  const openConsent = useCallback((type) => {
    const items = CONSENT_ITEMS_BY_COURSE[type] || [];
    const initialChecked = {};
    items.forEach((item) => { initialChecked[item.key] = false; });
    setConsentChecked(initialChecked);
    setConsentModal(type);
  }, []);

  const openBuy = useCallback((course) => setUpsellModal(course), []);
  const buyCombo = useCallback(() => openConsent('combo'), [openConsent]);

  const upsellAccept = useCallback(() => {
    setUpsellModal(null);
    openConsent('combo');
  }, [openConsent]);

  const upsellDecline = useCallback(() => {
    setUpsellModal((current) => {
      if (current) openConsent(current);
      return null;
    });
  }, [openConsent]);

  const handleConsentConfirm = useCallback(() => {
    if (consentModal) window.open(LINKS[consentModal], '_blank');
    setConsentModal(null);
  }, [consentModal]);

  const handleConsentToggle = useCallback((itemKey) => {
    setConsentChecked((prev) => ({ ...prev, [itemKey]: !prev[itemKey] }));
  }, []);

  const handleConsentClose = useCallback(() => setConsentModal(null), []);
  const handleUpsellChange = useCallback((open) => { if (!open) setUpsellModal(null); }, []);

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const headingFont = { fontFamily: "'Outfit', sans-serif" };

  return (
    <div className="bg-[#060B19] text-white min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* ——— HEADER ——— */}
      <header className="sticky top-0 z-50 bg-[#060B19]/85 backdrop-blur-xl border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <img src={POLITALKS_LOGO} alt="PoliTalks" className="h-16 w-auto" />
            <nav className="hidden lg:flex items-center gap-8 text-sm font-medium text-slate-400">
              {[['cursos','Cursos'],['comparativo','Comparativo'],['propaganda-detail','Propaganda Clássica'],['digital-detail','Direito Digital e IA'],['combo','Combo'],['professor','Professor'],['faq','FAQ'],['inscricao','Inscrição']].map(([id, label]) => (
                <button key={id} onClick={() => scrollTo(id)} className="hover:text-amber-400 transition-colors duration-200">{label}</button>
              ))}
            </nav>
            <Button data-testid="header-combo-btn" onClick={buyCombo} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-5 py-2 rounded-lg text-sm">
              Garantir Combo
            </Button>
          </div>
        </div>
      </header>

      {/* ——— URGENCY TOP BANNER ——— */}
      <UrgencyBanner comboSales={comboSales} urgencyConfig={urgencyConfig} comboPercent={comboPercent} />

      {/* ——— HERO ——— */}
      <section className="relative overflow-hidden pt-20 pb-28">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 via-[#060B19] to-amber-950/10 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="space-y-7 order-1">
              <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-bold px-4 py-2 rounded-full tracking-widest uppercase">
                <Shield className="w-4 h-4" />
                Atualizado — Res. TSE nº 23.755/2026
              </div>
              <div>
                <h1 className="text-5xl lg:text-6xl font-extrabold leading-[1.08] tracking-tight" style={headingFont}>
                  Cursos <span className="text-amber-400">Eleições<br />2026</span>
                </h1>
                <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-lg">
                  Propaganda Eleitoral, Direito Digital e Inteligência Artificial. Duas formações completas e atualizadas para quem precisa atuar com segurança jurídica nas campanhas de 2026 — das ruas às plataformas digitais.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {[
                  [BookOpen, "40h no combo"],
                  [Award, "Certificado incluso"],
                  [Calendar, "Até 20/12/2026"],
                ].map(([Icon, text]) => (
                  <span key={text} className="flex items-center gap-2 bg-slate-800/70 border border-slate-700/60 text-slate-300 text-sm px-4 py-2 rounded-full">
                    <Icon className="w-4 h-4 text-amber-400" />{text}
                  </span>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button data-testid="hero-combo-btn" onClick={buyCombo} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold py-4 px-8 text-base rounded-xl shadow-[0_0_30px_rgba(245,158,11,0.25)] hover:scale-105 transition-all duration-300">
                  Comprar Combo — R$ 2.699
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
                <Button variant="outline" onClick={() => scrollTo('cursos')} className="border-slate-700 text-slate-300 hover:bg-slate-800 bg-transparent py-4 px-8 text-base rounded-xl">
                  Ver os cursos
                </Button>
              </div>
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-semibold px-4 py-2 rounded-lg">
                <TrendingUp className="w-4 h-4" />
                Combo com economia de R$ 300,00 em relação à compra separada
              </div>
            </div>
            <div className="flex justify-center order-2">
              <div className="relative">
                <div className="absolute inset-0 bg-amber-500/15 blur-3xl rounded-full scale-110" />
                <img src={PROFESSOR_IMAGE} alt="Prof. Alexandre Basílio Coura" className="relative z-10 rounded-2xl shadow-2xl max-h-[540px] w-full object-cover" style={{ filter: 'drop-shadow(0 0 50px rgba(245,158,11,0.15))' }} />
                <div className="absolute -bottom-5 -right-4 bg-slate-900 border border-amber-500/30 rounded-xl p-4 z-20 shadow-2xl">
                  <div className="flex items-center gap-3">
                    <Star className="w-5 h-5 text-amber-400 shrink-0" />
                    <div>
                      <p className="text-white font-bold text-sm leading-tight">Prof. Alexandre Basílio Coura</p>
                      <p className="text-slate-400 text-xs">Direito Eleitoral · Digital · IA</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ——— COURSE CARDS ——— */}
      <section id="cursos" className="py-24 bg-[#0A0F1E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-3" style={headingFont}>Escolha sua Formação</h2>
            <p className="text-slate-400 text-lg">Adquira separadamente ou aproveite o combo com desconto especial.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Propaganda Card */}
            <div id="propaganda" className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-2xl p-8 flex flex-col hover:-translate-y-1 transition-transform duration-300 group">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  <div className="w-11 h-11 bg-orange-600/20 border border-orange-600/30 rounded-xl flex items-center justify-center shrink-0">
                    <Gavel className="w-5 h-5 text-orange-500" />
                  </div>
                  <Badge className="bg-orange-600/15 text-orange-400 border border-orange-600/30 text-xs font-semibold">20 horas • Certificado</Badge>
                  <Badge className="bg-emerald-600/15 text-emerald-400 border border-emerald-600/30 text-xs font-semibold flex items-center gap-1">
                    <PlayCircle className="w-3 h-3" />100% Gravado
                  </Badge>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-orange-400 transition-colors" style={headingFont}>Propaganda Eleitoral Clássica</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-3">Propaganda antecipada, propaganda de rua, bens públicos e particulares, comitês, material impresso, rádio e TV, assédio eleitoral e poder de polícia.</p>
                <div className="bg-emerald-500/8 border border-emerald-500/20 rounded-lg px-3 py-2.5">
                  <p className="text-emerald-400 text-xs font-semibold mb-0.5">Curso 100% assíncrono</p>
                  <p className="text-slate-400 text-xs">Aulas entregues gradualmente enquanto os processos são julgados pelo TSE. Gravações disponíveis até 31/12/2026.</p>
                </div>
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {propagandaHighlights.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />{item}
                  </li>
                ))}
              </ul>
              <div className="border-t border-slate-800 pt-6">
                <div className="mb-5">
                  <p className="text-slate-500 text-sm line-through">R$ 1.290,00</p>
                  <p className="text-3xl font-extrabold text-white">R$ 999<span className="text-lg font-medium">,00</span></p>
                  <p className="text-slate-500 text-xs mt-1">Cartão em até 6x sem juros · Pix ou Boleto à vista</p>
                </div>
                <div className="flex flex-col gap-2">
                  <Button data-testid="buy-propaganda-btn" onClick={() => openBuy('propaganda')} className="w-full bg-orange-600 hover:bg-orange-500 text-white font-bold py-3 rounded-xl">
                    Comprar este curso
                  </Button>
                  <Button variant="ghost" onClick={() => scrollTo('propaganda-detail')} className="w-full text-slate-500 hover:text-white hover:bg-slate-800 text-sm">
                    Ver programa completo <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Digital Card */}
            <div id="digital" className="bg-slate-900/60 backdrop-blur-md border border-violet-700/40 rounded-2xl overflow-hidden flex flex-col hover:-translate-y-1 transition-transform duration-300 group">

              {/* Topo com alerta de datas ao vivo */}
              <div className="bg-gradient-to-r from-violet-900/80 to-blue-900/60 border-b border-violet-700/40 px-6 py-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-extrabold px-3 py-1 rounded-full uppercase tracking-widest animate-pulse">
                    <Video className="w-3.5 h-3.5" />Ao Vivo
                  </span>
                  <span className="text-slate-300 text-xs font-semibold">06 a 10 de julho de 2026</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {['06/Jul','07/Jul','08/Jul','09/Jul','10/Jul'].map((d) => (
                    <span key={d} className="bg-violet-500/20 border border-violet-500/40 text-violet-200 text-xs font-bold px-2.5 py-1 rounded-lg font-mono">{d}</span>
                  ))}
                  <span className="flex items-center gap-1 text-slate-400 text-xs ml-1">
                    <Clock className="w-3 h-3" />18h às 22h
                  </span>
                </div>
              </div>

              <div className="p-8 flex flex-col flex-1">
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-5 flex-wrap">
                    <div className="w-11 h-11 bg-blue-600/20 border border-blue-600/30 rounded-xl flex items-center justify-center shrink-0">
                      <Cpu className="w-5 h-5 text-blue-400" />
                    </div>
                    <Badge className="bg-blue-600/15 text-blue-400 border border-blue-600/30 text-xs font-semibold">20 horas • Certificado</Badge>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors" style={headingFont}>Direito Digital e IA nas Campanhas de 2026</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">Propaganda eleitoral na internet, inteligência artificial, deepfakes, impulsionamento, plataformas digitais, mensageria, prova digital e remoção de conteúdo.</p>
                </div>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {digitalHighlights.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />{item}
                    </li>
                  ))}
                </ul>
                <div className="border-t border-slate-800 pt-6">
                  <div className="mb-3">
                    <p className="text-3xl font-extrabold text-white">R$ 2.000<span className="text-lg font-medium">,00</span></p>
                    <p className="text-slate-500 text-xs mt-1">Cartão em até 6x sem juros · Pix ou Boleto à vista</p>
                  </div>
                  <div className="flex items-start gap-2 bg-amber-500/8 border border-amber-500/25 rounded-lg px-3 py-2.5 mb-4">
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-amber-200/80 text-xs leading-relaxed"><strong className="text-amber-300">Atenção:</strong> sem acesso às gravações na compra isolada. Gravações disponíveis apenas no Combo.</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button data-testid="buy-digital-btn" onClick={() => openBuy('digital')} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl">
                      Comprar este curso
                    </Button>
                    <Button variant="ghost" onClick={() => scrollTo('digital-detail')} className="w-full text-slate-500 hover:text-white hover:bg-slate-800 text-sm">
                      Ver programa completo <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ——— COMBO ——— */}
      <section id="combo" className="py-24 bg-[#060B19]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-slate-900/80 border border-amber-500/40 rounded-3xl px-8 py-14 md:px-14 shadow-[0_0_80px_rgba(245,158,11,0.07)]">
            <div className="absolute -top-5 left-1/2 -translate-x-1/2">
              <span className="bg-amber-500 text-slate-950 font-extrabold px-6 py-2 rounded-full text-xs tracking-[0.15em] uppercase shadow-lg">
                Melhor Escolha
              </span>
            </div>
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4" style={headingFont}>
                Oferta Combo <span className="text-amber-400">Eleições 2026</span>
              </h2>
              <p className="text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed">
                Garanta as duas formações e tenha uma visão completa da propaganda eleitoral contemporânea: das ruas às plataformas digitais.
              </p>

              {/* Contador de vagas - 1ª turma */}
              <div data-testid="combo-vagas-card" className={`mt-7 inline-flex flex-col items-stretch gap-2 max-w-md w-full mx-auto rounded-xl border ${urgencyConfig.badge} px-5 py-4`}>
                <div className="flex items-center justify-between gap-3">
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-widest ${urgencyConfig.text}`}>
                    <AlertTriangle className="w-3.5 h-3.5" />
                    1ª Turma · Vagas limitadas
                  </span>
                  <span className="text-white font-mono text-sm font-bold">
                    {comboSales.combo_count}/{comboSales.combo_total}
                  </span>
                </div>
                <div className="relative h-2 bg-slate-900/60 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${urgencyConfig.bar} transition-all duration-700 ease-out`}
                    style={{ width: `${Math.max(2, comboPercent)}%` }}
                  />
                </div>
                <p className={`text-xs text-left ${urgencyConfig.text} font-semibold`}>{urgencyConfig.label}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              {[
                { label: "Propaganda Clássica", price: "R$ 999,00", sub: null, highlight: false },
                { label: "Direito Digital e IA", price: "R$ 2.000,00", sub: null, highlight: false },
                { label: "Combo (economia R$ 300)", price: "R$ 2.699,00", sub: "6x de R$ 449,83 sem juros", highlight: true },
              ].map((item, i) => (
                <div key={item.label} className={`rounded-2xl p-6 text-center border ${item.highlight ? 'bg-amber-500/10 border-amber-500/50' : 'bg-slate-800/40 border-slate-700'}`}>
                  {i === 1 && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-700 text-slate-400 text-xs px-3 py-0.5 rounded-full">+</div>
                  )}
                  <p className={`text-sm font-semibold mb-2 ${item.highlight ? 'text-amber-300' : 'text-slate-400'}`}>{item.label}</p>
                  <p className={`text-2xl font-extrabold ${item.highlight ? 'text-amber-400 text-3xl' : 'text-white'}`}>{item.price}</p>
                  {item.sub && <p className="text-slate-500 text-xs mt-1">{item.sub}</p>}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10">
              {[
                "Dois cursos completos — 40h",
                "Certificado total de 40 horas",
                "Aulas do Direito Digital gravadas — assista até 31/12",
                "Grupo exclusivo de WhatsApp",
                "Atualizações até 20/12/2026",
                "Visão completa da campanha",
              ].map((text) => (
                <div key={text} className="flex items-center gap-2 text-sm text-slate-300">
                  <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" />{text}
                </div>
              ))}
            </div>
            <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 mb-8 text-center">
              <p className="text-slate-300 text-sm"><strong className="text-white">Formas de pagamento:</strong> Em até 6x sem juros no cartão • PIX e Boleto à vista</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button data-testid="combo-buy-btn" onClick={buyCombo} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold py-5 px-12 text-xl rounded-2xl shadow-[0_0_40px_rgba(245,158,11,0.3)] hover:scale-105 transition-all duration-300">
                Quero o Combo com Desconto <ChevronRight className="w-6 h-6 ml-1" />
              </Button>
              <Button variant="outline" onClick={() => scrollTo('comparativo')} className="border-slate-700 text-slate-300 hover:bg-slate-800 bg-transparent py-5 px-8 rounded-2xl text-lg">
                Comparar cursos
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ——— WHY BOTH ——— */}
      <section className="py-24 bg-[#0A0F1E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-6" style={headingFont}>Por que a formação completa faz diferença em 2026?</h2>
            <p className="text-slate-400 text-lg leading-relaxed">
              As campanhas de 2026 não serão divididas entre mundo físico e digital. A propaganda eleitoral acontecerá simultaneamente nas ruas, nos comitês, no rádio, na TV, nas redes sociais, nos impulsionamentos, nos perfis de influenciadores e nos conteúdos produzidos por inteligência artificial.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { Icon: Scale, title: "Visão completa", desc: "Das ruas às plataformas digitais" },
              { Icon: Shield, title: "Prevenção de riscos", desc: "Identifique e responda ilícitos" },
              { Icon: TrendingUp, title: "Atualização normativa", desc: "Res. TSE 23.755/2026 inclusa" },
              { Icon: Gavel, title: "Atuação prática", desc: "Representações e fiscalização" },
              { Icon: Globe, title: "Domínio digital", desc: "IA, deepfakes e prova digital" },
              { Icon: Lock, title: "Segurança jurídica", desc: "Oriente candidatos com precisão" },
              { Icon: Users, title: "Para todos", desc: "Advogados, MPs, magistrados" },
              { Icon: Zap, title: "Resposta rápida", desc: "Eficiência em casos urgentes" },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 text-center hover:border-amber-500/30 transition-colors group">
                <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-amber-500/20 transition-colors">
                  <Icon className="w-5 h-5 text-amber-400" />
                </div>
                <h4 className="text-white font-semibold text-sm mb-1">{title}</h4>
                <p className="text-slate-500 text-xs">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ——— COMPARISON TABLE ——— */}
      <section id="comparativo" className="py-24 bg-[#060B19]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4" style={headingFont}>Comparativo dos Cursos</h2>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-slate-800">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="bg-slate-900 p-5 text-left text-slate-400 font-medium text-sm border-b border-slate-800 rounded-tl-2xl">Recurso</th>
                  <th className="bg-slate-900 p-5 text-center text-orange-400 font-semibold text-sm border-b border-slate-800">Propaganda Clássica</th>
                  <th className="bg-slate-900 p-5 text-center text-blue-400 font-semibold text-sm border-b border-slate-800">Direito Digital e IA</th>
                  <th className="bg-amber-500/10 p-5 text-center text-amber-400 font-bold text-sm border-b border-amber-500/30 rounded-tr-2xl">Combo Eleições 2026</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={row.feature} className={i % 2 !== 0 ? 'bg-slate-900/20' : ''}>
                    <td className="p-4 text-slate-300 text-sm border-b border-slate-800/40">{row.feature}</td>
                    <td className="p-4 text-center border-b border-slate-800/40"><CellIcon value={row.prop} /></td>
                    <td className="p-4 text-center border-b border-slate-800/40"><CellIcon value={row.dig} /></td>
                    <td className="p-4 text-center border-b border-amber-500/10 bg-amber-500/5"><CellIcon value={row.combo} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-8 justify-center mt-8 text-sm text-slate-400">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-400" /> Incluso</span>
            <span className="flex items-center gap-2"><MinusCircle className="w-4 h-4 text-amber-400" /> Parcial</span>
            <span className="flex items-center gap-2"><XCircle className="w-4 h-4 text-slate-600" /> Não incluso</span>
          </div>
        </div>
      </section>

      {/* ——— PROPAGANDA DETAIL ——— */}
      <section id="propaganda-detail" className="py-24 bg-[#0A0F1E]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="w-12 h-12 bg-orange-600/20 border border-orange-600/30 rounded-xl flex items-center justify-center shrink-0 mt-1">
              <Gavel className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <p className="text-orange-400 text-xs font-bold uppercase tracking-widest mb-1">Curso Completo — 20 horas</p>
              <h2 className="text-3xl font-bold text-white" style={headingFont}>Propaganda Eleitoral Clássica</h2>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-xs font-semibold px-3 py-1.5 rounded-full">
              <PlayCircle className="w-3.5 h-3.5" />100% Gravado — Assíncrono
            </span>
            <span className="inline-flex items-center gap-1.5 bg-slate-800 border border-slate-700 text-slate-300 text-xs px-3 py-1.5 rounded-full">
              <Calendar className="w-3.5 h-3.5 text-amber-400" />Gravações disponíveis até 31/12/2026
            </span>
          </div>
          <div className="bg-emerald-500/8 border border-emerald-500/20 rounded-xl px-5 py-4 mb-8">
            <p className="text-emerald-300 text-sm font-semibold mb-1">Modalidade: assíncrono (100% gravado)</p>
            <p className="text-slate-400 text-sm leading-relaxed">As aulas são entregues gradualmente conforme os processos eleitorais são julgados pelo TSE, garantindo conteúdo hiper-atualizado. As gravações ficam disponíveis para todos os alunos até 31 de dezembro de 2026.</p>
          </div>
          <p className="text-slate-400 text-lg leading-relaxed mb-10">
            A propaganda eleitoral clássica continua sendo uma das áreas mais relevantes e fiscalizadas das campanhas. O curso oferece uma abordagem prática e atualizada para quem precisa orientar campanhas, prevenir irregularidades, responder notificações, formular representações e compreender os limites da propaganda nos espaços físicos e nos meios tradicionais.
          </p>
          <div className="mb-10">
            <h3 className="text-lg font-semibold text-white mb-5">Programa do Curso — 6 Módulos</h3>
            <Accordion type="single" collapsible className="space-y-2">
              {propagandaModules.map((mod, i) => (
                <AccordionItem key={mod.title} value={`prop-${i}`} className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden data-[state=open]:border-orange-600/30">
                  <AccordionTrigger className="px-6 py-4 text-left text-slate-200 font-semibold hover:text-orange-400 hover:no-underline transition-colors">
                    <div className="flex items-center gap-3 text-left">
                      <span className="text-orange-500 font-mono text-xs shrink-0">0{i + 1}</span>
                      <span className="text-sm">{mod.title}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-5">
                    <ul className="space-y-2.5 ml-8">
                      {mod.topics.map((t) => (
                        <li key={t} className="flex items-start gap-2 text-sm text-slate-400">
                          <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 shrink-0" />{t}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* ——— CALENDÁRIO DE ENTREGAS ——— */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-orange-600/20 border border-orange-600/30 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Calendário de Entregas das Aulas</h3>
                <p className="text-slate-500 text-xs mt-0.5">Todas as segundas-feiras • 10 aulas ao longo do semestre</p>
              </div>
            </div>

            <div className="relative pl-4">
              {/* Linha vertical */}
              <div className="absolute left-0 top-2 bottom-2 w-px bg-gradient-to-b from-orange-500/60 via-orange-500/30 to-orange-500/10" />

              <div className="space-y-0">
                {propagandaCalendar.map((item, i) => (
                  <div key={item.full} className="relative flex items-start gap-4 pb-5 group">
                    {/* Ponto na timeline */}
                    <div className="absolute -left-[4px] mt-2 w-2 h-2 rounded-full bg-orange-500 shrink-0 group-hover:scale-125 transition-transform duration-200" />

                    {/* Data badge */}
                    <div className="shrink-0 ml-4">
                      <span className="inline-block text-orange-400 font-bold text-xs font-mono bg-orange-500/10 border border-orange-500/25 px-2.5 py-1.5 rounded-lg min-w-[52px] text-center">
                        {item.date}
                      </span>
                      <p className="text-slate-600 text-[10px] text-center mt-1">{item.full.slice(6)}</p>
                    </div>

                    {/* Conteúdo */}
                    <div className="flex-1 pt-1 pb-1">
                      <p className="text-slate-300 text-sm leading-relaxed group-hover:text-white transition-colors">
                        {i === 0 && <span className="inline-flex items-center bg-orange-600/20 text-orange-300 text-[10px] font-bold px-2 py-0.5 rounded mr-2 uppercase tracking-wide">Abertura</span>}
                        {item.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Nota sobre vídeos extras */}
            <div className="mt-5 bg-amber-500/8 border border-amber-500/20 rounded-xl px-5 py-4 flex items-start gap-3">
              <Zap className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-slate-400 text-sm leading-relaxed">
                <strong className="text-amber-300">Bônus:</strong> Conforme as novidades forem surgindo, o Professor poderá disponibilizar vídeos curtos sobre temas importantes após decisões da Justiça Eleitoral, até a data do pleito.
              </p>
            </div>
          </div>
          <div className="bg-slate-900/60 border border-orange-600/30 rounded-2xl p-8 text-center">
            <p className="text-slate-500 text-sm line-through mb-1">De R$ 1.290,00</p>
            <p className="text-4xl font-extrabold text-white mb-1">R$ 999,00</p>
            <p className="text-slate-500 text-sm mb-6">Em até 6x sem juros no cartão • PIX e Boleto à vista</p>
            <Button data-testid="buy-propaganda-detail-btn" onClick={() => openBuy('propaganda')} className="bg-orange-600 hover:bg-orange-500 text-white font-bold py-4 px-10 text-base rounded-xl">
              Comprar Propaganda Eleitoral Clássica
            </Button>
          </div>
        </div>
      </section>

      {/* ——— DIGITAL DETAIL ——— */}
      <section id="digital-detail" className="py-24 bg-[#060B19]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="w-12 h-12 bg-blue-600/20 border border-blue-600/30 rounded-xl flex items-center justify-center shrink-0 mt-1">
              <Cpu className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-1">Curso Completo — 20 horas</p>
              <h2 className="text-3xl font-bold text-white" style={headingFont}>Direito Digital e Inteligência Artificial nas Campanhas de 2026</h2>
            </div>
          </div>

          {/* ——— BLOCO DE DATAS PROMINENTE ——— */}
          <div className="bg-gradient-to-br from-violet-950/80 to-blue-950/60 border border-violet-600/40 rounded-2xl overflow-hidden mb-8 shadow-[0_0_40px_rgba(139,92,246,0.08)]">
            {/* Header do bloco */}
            <div className="bg-violet-600/20 border-b border-violet-600/30 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/40 text-red-300 text-sm font-extrabold px-4 py-1.5 rounded-full uppercase tracking-wider">
                  <Video className="w-4 h-4" />Curso ao Vivo
                </span>
                <span className="text-white font-bold text-lg">06 a 10 de julho de 2026</span>
              </div>
              <span className="flex items-center gap-2 bg-violet-500/20 border border-violet-500/30 text-violet-200 text-sm font-semibold px-4 py-1.5 rounded-full">
                <Clock className="w-4 h-4" />18h às 22h — diariamente
              </span>
            </div>

            {/* Grid dos 5 dias */}
            <div className="grid grid-cols-5 gap-0">
              {[
                { dia: 'SEG', data: '06', mes: 'JUL' },
                { dia: 'TER', data: '07', mes: 'JUL' },
                { dia: 'QUA', data: '08', mes: 'JUL' },
                { dia: 'QUI', data: '09', mes: 'JUL' },
                { dia: 'SEX', data: '10', mes: 'JUL' },
              ].map((item, i) => (
                <div key={item.data} className={`flex flex-col items-center py-5 border-r border-violet-700/20 last:border-r-0 ${i === 0 ? 'bg-violet-500/10' : ''}`}>
                  <span className="text-violet-400 text-xs font-bold uppercase tracking-widest mb-1">{item.dia}</span>
                  <span className="text-white text-3xl font-extrabold leading-none" style={headingFont}>{item.data}</span>
                  <span className="text-slate-500 text-xs font-semibold uppercase mt-1">{item.mes} 2026</span>
                </div>
              ))}
            </div>

            {/* Alerta de gravação */}
            <div className="border-t border-violet-700/30 grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-800/60">
              <div className="flex items-start gap-3 px-6 py-4">
                <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-emerald-300 text-sm font-semibold">Gravação inclusa no Combo</p>
                  <p className="text-slate-400 text-xs mt-0.5">Alunos do Combo assistem às gravações até 31/12/2026</p>
                </div>
              </div>
              <div className="flex items-start gap-3 px-6 py-4">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-300 text-sm font-semibold">Sem gravação na compra isolada</p>
                  <p className="text-slate-400 text-xs mt-0.5">Alunos sem o Combo precisam assistir ao vivo nas datas acima</p>
                </div>
              </div>
            </div>
          </div>
          <p className="text-slate-400 text-lg leading-relaxed mb-10">
            As campanhas eleitorais de 2026 serão marcadas pelo uso de inteligência artificial, redes sociais, impulsionamentos, influenciadores, perfis falsos, mensagens privadas e disputas judiciais sobre remoção de conteúdo e desinformação. Este curso foi desenvolvido para quem precisa atuar com segurança jurídica no ambiente digital.
          </p>
          <div className="mb-10">
            <h3 className="text-lg font-semibold text-white mb-5">Programa do Curso — 8 Módulos</h3>
            <Accordion type="single" collapsible className="space-y-2">
              {digitalModules.map((mod, i) => (
                <AccordionItem key={mod.title} value={`dig-${i}`} className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden data-[state=open]:border-blue-600/30">
                  <AccordionTrigger className="px-6 py-4 text-left text-slate-200 font-semibold hover:text-blue-400 hover:no-underline transition-colors">
                    <div className="flex items-center gap-3 text-left">
                      <span className="text-blue-400 font-mono text-xs shrink-0">{String(i + 1).padStart(2, '0')}</span>
                      <span className="text-sm">{mod.title}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-5">
                    <ul className="space-y-2.5 ml-8">
                      {mod.topics.map((t) => (
                        <li key={t} className="flex items-start gap-2 text-sm text-slate-400">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 shrink-0" />{t}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <div className="bg-slate-900/60 border border-blue-600/30 rounded-2xl p-8 text-center">
            <p className="text-4xl font-extrabold text-white mb-1">R$ 2.000,00</p>
            <p className="text-slate-500 text-sm mb-6">Em até 6x sem juros no cartão • PIX e Boleto à vista</p>
            <Button data-testid="buy-digital-detail-btn" onClick={() => openBuy('digital')} className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 px-10 text-base rounded-xl">
              Comprar Direito Digital e IA
            </Button>
          </div>
        </div>
      </section>

      {/* ——— ADVANTAGES ——— */}
      <section id="vantagens" className="py-24 bg-[#0A0F1E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4" style={headingFont}>Vantagens para Alunos</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { Icon: Calendar, title: "Atualização até 20/12/2026", desc: "Os alunos receberão atualizações sobre mudanças normativas, decisões relevantes e temas sensíveis das eleições de 2026 até 20 de dezembro de 2026.", highlight: false },
              { Icon: Award, title: "Certificado", desc: "Cada curso possui certificado individual de 20 horas. No combo, o aluno recebe certificado total de 40 horas.", highlight: false },
              { Icon: BookOpen, title: "Aulas Gravadas do Direito Digital", desc: "Alunos do combo poderão assistir às aulas do Curso de Direito Digital gravadas até 31 de dezembro de 2026. Esta vantagem não está disponível na compra isolada do curso. (Exclusivo Combo)", highlight: true },
              { Icon: MessageCircle, title: "Grupo Exclusivo de WhatsApp", desc: "Alunos do combo terão acesso a grupo exclusivo para avisos, atualizações, materiais complementares e troca de informações. (Exclusivo Combo)", highlight: true },
              { Icon: Gavel, title: "Conteúdo Prático e Atualizado", desc: "Os cursos foram pensados para a atuação real em campanhas, consultorias, contencioso eleitoral, fiscalização e prevenção de riscos.", highlight: false },
              { Icon: Shield, title: "Res. TSE nº 23.755/2026", desc: "Os dois cursos incorporam as alterações trazidas pela Resolução TSE nº 23.755/2026 sobre propaganda eleitoral, internet e IA.", highlight: false },
            ].map(({ Icon, title, desc, highlight }) => (
              <div key={title} className={`rounded-2xl p-8 border ${highlight ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-800 bg-slate-900/60'}`}>
                <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-white font-bold text-lg mb-3">{title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ——— PROFESSOR ——— */}
      <section id="professor" className="py-24 bg-[#060B19]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-amber-500/10 blur-3xl rounded-full scale-110" />
                <img src={PROFESSOR_IMAGE} alt="Prof. Alexandre Basílio Coura" className="relative z-10 rounded-2xl shadow-2xl max-w-[400px] w-full object-cover" />
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 mb-5">Com quem você vai aprender</Badge>
                <h2 className="text-4xl font-bold text-white mb-4" style={headingFont}>Prof. Alexandre Basílio Coura</h2>
                <p className="text-slate-400 text-lg leading-relaxed mb-4">
                  Especialista em Direito Eleitoral, Direito Digital e Cibersegurança, com atuação voltada à interseção entre tecnologia, propaganda eleitoral, provas digitais, inteligência artificial e campanhas políticas.
                </p>
                <p className="text-slate-400 leading-relaxed">
                  Professor, pesquisador e profissional com experiência prática na análise de ilícitos eleitorais, propaganda na internet, investigações digitais e IA aplicada ao Direito. Une abordagem técnica, jurídica e prática para preparar profissionais para os desafios das eleições de 2026.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {["Direito Eleitoral", "Direito Digital", "Inteligência Artificial", "Cibersegurança", "Prova Digital", "Campanhas Eleitorais", "Eleições 2026"].map((tag) => (
                  <span key={tag} className="bg-slate-800 border border-slate-700 text-slate-300 text-xs px-3 py-1.5 rounded-full font-medium">{tag}</span>
                ))}
              </div>
              <div className="pt-2">
                <Button data-testid="professor-combo-btn" onClick={buyCombo} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold py-3 px-8 rounded-xl">
                  Garantir minha vaga no combo
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ——— PRICING ——— */}
      <section id="inscricao" className="py-24 bg-[#0A0F1E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4" style={headingFont}>Escolha seu Plano</h2>
            <p className="text-slate-400 text-lg">Formações atualizadas para as Eleições 2026</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Propaganda */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8">
              <p className="text-orange-400 font-bold text-xs uppercase tracking-widest mb-3">Propaganda Clássica</p>
              <h3 className="text-xl font-bold text-white mb-5" style={headingFont}>Propaganda Eleitoral</h3>
              <p className="text-slate-500 text-sm line-through">R$ 1.290,00</p>
              <p className="text-4xl font-extrabold text-white mb-1">R$ 999<span className="text-xl">,00</span></p>
              <p className="text-slate-500 text-xs mb-8">6x de R$ 166,50 sem juros</p>
              <ul className="space-y-3 mb-8">
                {["20 horas de formação", "Certificado de 20h", "Res. TSE 23.755/2026", "6x sem juros no cartão", "PIX e Boleto à vista"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-orange-500 shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Button data-testid="pricing-propaganda-btn" onClick={() => openBuy('propaganda')} className="w-full bg-orange-600 hover:bg-orange-500 text-white font-bold py-3 rounded-xl">
                Comprar Propaganda
              </Button>
            </div>

            {/* Combo - Center Featured */}
            <div className="relative bg-slate-900/90 border-2 border-amber-500 rounded-2xl p-8 shadow-[0_0_60px_rgba(245,158,11,0.12)] -mt-4 lg:-mt-4">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-amber-500 text-slate-950 font-extrabold text-xs px-5 py-1.5 rounded-full tracking-widest uppercase shadow-lg">Melhor Custo-Benefício</span>
              </div>
              <p className="text-amber-400 font-bold text-xs uppercase tracking-widest mb-3">Formação Completa</p>
              <h3 className="text-xl font-bold text-white mb-5" style={headingFont}>Combo Eleições 2026</h3>
              <p className="text-slate-500 text-sm"><span className="line-through">R$ 2.999,00</span> — <span className="text-emerald-400 font-semibold">economia R$ 300</span></p>
              <p className="text-5xl font-extrabold text-amber-400 mt-1 mb-1">R$ 2.699<span className="text-2xl">,00</span></p>
              <p className="text-slate-500 text-xs mb-8">6x de R$ 449,83 sem juros</p>
              <ul className="space-y-3 mb-8">
                {["Dois cursos completos", "40 horas de formação", "Certificado de 40h", "Aulas gravadas — Direito Digital até 31/12", "Grupo exclusivo de WhatsApp", "Atualizações até 20/12/2026", "6x sem juros no cartão", "PIX e Boleto à vista"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-white">
                    <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Button data-testid="pricing-combo-btn" onClick={buyCombo} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold py-4 rounded-xl text-base hover:scale-105 transition-all duration-300 shadow-[0_0_20px_rgba(245,158,11,0.2)]">
                Comprar Combo
              </Button>
            </div>

            {/* Digital */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8">
              <p className="text-blue-400 font-bold text-xs uppercase tracking-widest mb-3">Direito Digital</p>
              <h3 className="text-xl font-bold text-white mb-5" style={headingFont}>Direito Digital e IA</h3>
              <div className="h-5 mb-0" />
              <p className="text-4xl font-extrabold text-white mb-1">R$ 2.000<span className="text-xl">,00</span></p>
              <p className="text-slate-500 text-xs mb-8">6x de R$ 333,33 sem juros</p>
              <ul className="space-y-3 mb-8">
                {["20 horas de formação", "Certificado de 20h", "Res. TSE 23.755/2026", "6x sem juros no cartão", "PIX e Boleto à vista"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-blue-400 shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Button data-testid="pricing-digital-btn" onClick={() => openBuy('digital')} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl">
                Comprar Direito Digital e IA
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ——— FAQ ——— */}
      <section id="faq" className="py-24 bg-[#060B19]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4" style={headingFont}>Perguntas Frequentes</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqItems.map((item) => (
              <AccordionItem key={item.q} value={item.q} className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                <AccordionTrigger className="px-6 py-4 text-left text-slate-200 font-semibold hover:text-amber-400 hover:no-underline transition-colors text-sm">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5 text-slate-400 text-sm leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ——— FOOTER ——— */}
      <footer className="bg-slate-950 border-t border-slate-800/60 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
            <div>
              <img src={POLITALKS_LOGO} alt="PoliTalks" className="h-20 w-auto mb-4" />
              <p className="text-slate-500 text-sm leading-relaxed">Educação jurídica de excelência com foco em tecnologia, Direito Digital e Direito Eleitoral.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Cursos Eleições 2026</h4>
              <div className="space-y-2 text-slate-500 text-sm">
                <p>Propaganda Eleitoral Clássica — R$ 999,00</p>
                <p>Direito Digital e IA — R$ 2.000,00</p>
                <p>Combo Eleições 2026 — R$ 2.699,00</p>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Contato</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Mail className="w-4 h-4 shrink-0" />
                  <span>comercial@politalks.com.br</span>
                </div>
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Phone className="w-4 h-4 shrink-0" />
                  <span>(31) 9 7555-1877</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800/50 pt-8 text-center text-slate-600 text-sm">
            <p>&copy; 2026 PoliTalks. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      {/* ——— UPSELL MODAL ——— */}
      <UpsellModal
        upsellModal={upsellModal}
        onAccept={upsellAccept}
        onDecline={upsellDecline}
        onOpenChange={handleUpsellChange}
      />

      {/* ——— CONSENT MODAL (LGPD - todos os cursos) ——— */}
      <ConsentModal
        consentModal={consentModal}
        consentChecked={consentChecked}
        onToggleItem={handleConsentToggle}
        onConfirm={handleConsentConfirm}
        onClose={handleConsentClose}
      />

      {/* ——— FLOATING CTA ——— */}
      {showFloat && (
        <div className="fixed bottom-8 right-8 z-40 animate-[fadeIn_0.3s_ease-out]">
          <Button data-testid="floating-combo-btn" onClick={buyCombo} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold px-6 py-4 rounded-2xl shadow-[0_8px_40px_rgba(245,158,11,0.35)] hover:scale-105 transition-all duration-300">
            Comprar Combo <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};
